import { create } from 'zustand';
import { Conversation, ConversationFilter } from '../types';

interface ConversationState {
  conversations: Conversation[];
  selectedConversationId: string | null;
  filter: ConversationFilter;
  searchQuery: string;
  /** Show only conversations on this business number (null: every number). */
  numberFilter: { id: string; isDefault: boolean } | null;

  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;
  selectConversation: (id: string | null) => void;
  setFilter: (filter: ConversationFilter) => void;
  setSearchQuery: (query: string) => void;
  setNumberFilter: (numberFilter: { id: string; isDefault: boolean } | null) => void;
  getConversation: (id: string) => Conversation | undefined;
  getFilteredConversations: () => Conversation[];
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  conversations: [],
  selectedConversationId: null,
  filter: 'all',
  searchQuery: '',
  numberFilter: null,

  setConversations: (conversations) => {
    set({ conversations });
  },

  addConversation: (conversation) => {
    set((state) => ({
      conversations: [conversation, ...state.conversations],
    }));
  },

  updateConversation: (id, updates) => {
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === id ? { ...conv, ...updates } : conv
      ),
    }));
  },

  removeConversation: (id) => {
    set((state) => ({
      conversations: state.conversations.filter((conv) => conv.id !== id),
      selectedConversationId:
        state.selectedConversationId === id ? null : state.selectedConversationId,
    }));
  },

  selectConversation: (id) => {
    set({ selectedConversationId: id });
  },

  setFilter: (filter) => {
    set({ filter });
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  setNumberFilter: (numberFilter) => {
    set({ numberFilter });
  },

  getConversation: (id) => {
    return get().conversations.find((conv) => conv.id === id);
  },

  getFilteredConversations: () => {
    const { conversations, filter, searchQuery, numberFilter } = get();
    const user = useAuthStore.getState().user;

    let filtered = conversations;

    // Apply filter
    switch (filter) {
      case 'assigned_to_me':
        filtered = conversations.filter((conv) => conv.assignedStaffId === user?.id);
        break;
      case 'unassigned':
        filtered = conversations.filter((conv) => !conv.assignedStaffId);
        break;
      case 'bot_active':
        filtered = conversations.filter((conv) => conv.state === 'bot_active');
        break;
      case 'staff_active':
        filtered = conversations.filter((conv) => conv.state === 'staff_active');
        break;
      case 'resolved':
        filtered = conversations.filter((conv) => conv.state === 'resolved');
        break;
      case 'negative_sentiment':
        filtered = conversations.filter((conv) => conv.sentiment === 'negative');
        break;
      case 'high_priority':
        filtered = conversations.filter(
          (conv) => conv.priority === 'high' || conv.priority === 'urgent'
        );
        break;
      case 'voice_calls':
        filtered = conversations.filter((conv) => conv.channel === 'voice');
        break;
      default:
        // 'all' - no filtering
        break;
    }

    // Business number: a conversation with none recorded is on the default.
    if (numberFilter) {
      filtered = filtered.filter((conv) =>
        conv.whatsappNumberId
          ? conv.whatsappNumberId === numberFilter.id
          : numberFilter.isDefault && conv.channel !== 'web'
      );
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (conv) =>
          (conv.customerName || '').toLowerCase().includes(query) ||
          (conv.customerPhone || '').includes(query) ||
          (conv.lastMessage || '').toLowerCase().includes(query)
      );
    }

    // Sort by last activity (most recent first)
    return filtered.sort(
      (a, b) => new Date(b.lastActivity || 0).getTime() - new Date(a.lastActivity || 0).getTime()
    );
  },
}));

// Import authStore - needed for filtering
import { useAuthStore } from './authStore';
